import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import {
    LoadAllKpis,
    LoadAllLocations,
    LoadAllOrgUnits,
    LoadAllSubjectAreas,
    LoadAllUnits
} from '@tran-monorepo/frontend/states';
import { SortOrder } from '@tran-monorepo/interfaces';
import { Application, ApplicationStatusEnum } from '@tran-monorepo/interfaces/applications';
import { Observable, Subject, forkJoin, of, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ApplicationsService } from '../../../../shared/services/applications.service';
import { SubmitResult } from '../../models/application-form.model';
import {
    CancelApplicationLoading,
    ClearMessage,
    CreateApplication,
    CreateApplicationSilent,
    LoadApplication,
    LoadDraftApplication,
    LoadReferenceData,
    PatchApplication,
    ResetApplicationFormState,
    SaveDraftFailure,
    SaveDraftSuccess,
    SubmitApplication,
    SubmitFailure,
    SubmitSuccess
} from './application-form.actions';

export type ApplicationFormStateModel = {
    application: Application | null;
    draftApplication: Application | null;
    isLoading: boolean;
    isLoadingReferenceData: boolean;
    savingDraft: boolean;
    submitting: boolean;
    submitResult: SubmitResult | null;
    message: string | null;
    error: string | null;
    lastSavedAt: string | null;
};

@State<ApplicationFormStateModel>({
    name: 'applicationForm',
    defaults: {
        application: null,
        draftApplication: null,
        isLoading: false,
        isLoadingReferenceData: false,
        savingDraft: false,
        submitting: false,
        submitResult: null,
        message: null,
        error: null,
        lastSavedAt: null
    }
})
@Injectable()
export class ApplicationFormState {
    private readonly applicationService = inject(ApplicationsService);
    private readonly store = inject(Store);
    private readonly translate = inject(TranslateService);
    private readonly cancelLoad$ = new Subject<void>();

    @Selector()
    static application(state: ApplicationFormStateModel): Application | null {
        return state.application;
    }

    @Selector()
    static draftApplication(state: ApplicationFormStateModel): Application | null {
        return state.draftApplication;
    }

    @Selector()
    static isLoading(state: ApplicationFormStateModel): boolean {
        return state.isLoading || state.isLoadingReferenceData;
    }

    @Selector()
    static isLoadingReferenceData(state: ApplicationFormStateModel): boolean {
        return state.isLoadingReferenceData;
    }

    @Selector() static savingDraft(state: ApplicationFormStateModel) {
        return state.savingDraft;
    }

    @Selector() static submitting(state: ApplicationFormStateModel) {
        return state.submitting;
    }

    @Selector() static message(state: ApplicationFormStateModel) {
        return state.message;
    }

    @Selector() static error(state: ApplicationFormStateModel) {
        return state.error;
    }

    @Selector() static lastSavedAt(state: ApplicationFormStateModel) {
        return state.lastSavedAt;
    }

    @Selector() static submitResult(state: ApplicationFormStateModel) {
        return state.submitResult;
    }

    @Action(CancelApplicationLoading)
    cancelApplicationLoading(): void {
        this.cancelLoad$.next();
    }

    @Action(CreateApplication)
    createApplication(ctx: StateContext<ApplicationFormStateModel>, { payload }: CreateApplication): Observable<void> {
        ctx.patchState({ savingDraft: true, message: null, error: null });
        return this.applicationService.createApplication(payload).pipe(
            switchMap(createdApplication =>
                this.applicationService.getApplicationKpis(createdApplication.id).pipe(
                    map(kpis => ({ ...createdApplication, kpis })),
                    catchError(() => of(createdApplication))
                )
            ),
            tap(createdApplication => {
                ctx.patchState({
                    application: createdApplication
                });
            }),
            map(() => void 0),
            catchError(err => {
                ctx.patchState({
                    message: null,
                    error:
                        err?.message ??
                        this.translate.instant('application-form.notifications.create-application.error')
                });
                return throwError(() => err);
            }),
            finalize(() => ctx.patchState({ savingDraft: false }))
        );
    }

    @Action(CreateApplicationSilent)
    createApplicationSilent(
        ctx: StateContext<ApplicationFormStateModel>,
        { payload }: CreateApplicationSilent
    ): Observable<void> {
        return this.createApplication(ctx, new CreateApplication(payload));
    }

    @Action(PatchApplication)
    patchApplication(
        ctx: StateContext<ApplicationFormStateModel>,
        { applicationId, payload }: PatchApplication
    ): Observable<void> {
        ctx.patchState({ savingDraft: true, message: null, error: null });
        return this.applicationService.updateApplication(applicationId, payload).pipe(
            switchMap(updatedApplication =>
                this.applicationService.getApplicationKpis(applicationId).pipe(
                    map(kpis => ({ ...updatedApplication, kpis })),
                    catchError(() => of(updatedApplication))
                )
            ),
            tap(updatedApplication => {
                ctx.patchState({
                    application: updatedApplication
                });
            }),
            map(() => void 0),
            catchError(err => {
                ctx.patchState({
                    message: null,
                    error:
                        err?.message ??
                        this.translate.instant('application-form.notifications.update-application.error')
                });
                return throwError(() => err);
            }),
            finalize(() => ctx.patchState({ savingDraft: false }))
        );
    }

    @Action(LoadApplication)
    loadApplication(
        ctx: StateContext<ApplicationFormStateModel>,
        { applicationId }: LoadApplication
    ): Observable<Application> {
        ctx.patchState({ isLoading: true });
        return forkJoin({
            application: this.applicationService.getApplication(applicationId),
            kpis: this.applicationService.getApplicationKpis(applicationId)
        }).pipe(
            takeUntil(this.cancelLoad$),
            map(({ application, kpis }) => ({
                ...application,
                kpis
            })),
            tap(application => ctx.patchState({ application })),
            catchError(error => {
                ctx.patchState({
                    error:
                        error?.message ??
                        this.translate.instant('application-form.notifications.load-application.error')
                });
                return throwError(() => error);
            }),
            finalize(() => ctx.patchState({ isLoading: false }))
        );
    }

    @Action(LoadDraftApplication)
    loadDraftApplication(ctx: StateContext<ApplicationFormStateModel>): Observable<Application[]> {
        ctx.patchState({ isLoading: true });
        return this.applicationService
            .getUserApplications({ page: 0, pageSize: 1, orderBy: 'updatedAt', sortOrder: SortOrder.DESC }, [
                ApplicationStatusEnum.Draft
            ])
            .pipe(
                tap(({ items }) => {
                    const draftApplication = items.length > 0 ? items[0] : null;
                    ctx.patchState({ draftApplication });
                }),
                finalize(() => ctx.patchState({ isLoading: false })),
                map(({ items }) => items)
            );
    }

    @Action(LoadReferenceData)
    loadReferenceData(ctx: StateContext<ApplicationFormStateModel>): Observable<void> {
        ctx.patchState({ isLoadingReferenceData: true });
        return this.store
            .dispatch([
                new LoadAllLocations(),
                new LoadAllOrgUnits(),
                new LoadAllKpis(),
                new LoadAllUnits(),
                new LoadAllSubjectAreas()
            ])
            .pipe(
                catchError(error => {
                    ctx.patchState({
                        error:
                            error?.message ??
                            this.translate.instant('application-form.notifications.load-reference-data.error'),
                        message: null
                    });
                    return throwError(() => error);
                }),
                finalize(() => ctx.patchState({ isLoadingReferenceData: false }))
            );
    }

    @Action(ResetApplicationFormState)
    resetState(ctx: StateContext<ApplicationFormStateModel>): void {
        ctx.setState({
            application: null,
            draftApplication: null,
            isLoading: false,
            isLoadingReferenceData: false,
            savingDraft: false,
            submitting: false,
            submitResult: null,
            message: null,
            error: null,
            lastSavedAt: null
        });
    }

    @Action(SaveDraftSuccess)
    saveDraftSuccess(ctx: StateContext<ApplicationFormStateModel>, { result }: SaveDraftSuccess): void {
        ctx.patchState({
            savingDraft: false,
            lastSavedAt: result.savedAt,
            message: this.translate.instant('application-form.notifications.save-draft.success'),
            error: null
        });
    }

    @Action(SaveDraftFailure)
    saveDraftFailure(ctx: StateContext<ApplicationFormStateModel>, { error }: SaveDraftFailure): void {
        ctx.patchState({ savingDraft: false, error, message: null });
    }

    @Action(SubmitApplication)
    submit(
        ctx: StateContext<ApplicationFormStateModel>,
        { applicationId, payload }: SubmitApplication
    ): Observable<void> {
        ctx.patchState({ submitting: true, submitResult: null, message: null, error: null });

        const saveOperation$ = applicationId
            ? this.applicationService.updateApplication(applicationId, payload)
            : this.applicationService.createApplication(payload);

        return saveOperation$.pipe(
            tap(savedApplication => {
                ctx.patchState({ application: savedApplication });
            }),
            switchMap(savedApplication => {
                return this.applicationService.submitApplication(savedApplication.id).pipe(
                    tap(() => {
                        ctx.patchState({
                            message: this.translate.instant('application-form.notifications.submit-application.success')
                        });
                    })
                );
            }),
            catchError(error => {
                ctx.patchState({
                    error:
                        error?.message ??
                        this.translate.instant('application-form.notifications.submit-application.error'),
                    message: null
                });
                return throwError(() => error);
            }),
            finalize(() => ctx.patchState({ submitting: false }))
        );
    }

    @Action(SubmitSuccess)
    submitSuccess(ctx: StateContext<ApplicationFormStateModel>, { result }: SubmitSuccess): void {
        ctx.patchState({
            submitting: false,
            submitResult: result,
            message: this.translate.instant('application-form.notifications.submit-application.success-with-id', {
                applicationId: result.applicationId
            }),
            error: null
        });
    }

    @Action(SubmitFailure)
    submitFailure(ctx: StateContext<ApplicationFormStateModel>, { error }: SubmitFailure): void {
        ctx.patchState({ submitting: false, error, message: null });
    }

    @Action(ClearMessage)
    clearMessage(ctx: StateContext<ApplicationFormStateModel>): void {
        ctx.patchState({ message: null, error: null });
    }
}
